// The home screen inside DJI Pilot 2: the start sequence (license, serials,
// tokens, registration, modules) and what it shows — the Flutter page's
// dji_home_screeen.dart, presentation/left and presentation/right, and
// dji_controller_repository.dart (autrik_codex T90.1, T90.3, T90.4).
//
// Laid out as the Autrik Console design system's detail page (screens.md §3),
// fitted to what DJI Pilot 2 leaves it: about 768 × 390 CSS px on an RC Pro
// Enterprise (1920 × 1080 at density 400), under Pilot's own bar, which
// already names the platform and carries its Log Out — so no page header of
// our own. Two panels side by side that fill the height and scroll inside:
// Controller (the serials, registration, the account) and a panel with a tab
// row: Modules (a grid), Settings (form rows), Start (Pilot's answers).

import { type Bridge, findBridge } from './bridge';
import type { Config } from './config';
import { type Device, type Session, createDevice, emqxToken, listDevices, livekitToken, setDeviceSdk } from './api';
import {
  AIRCRAFT,
  type DeviceModel,
  REMOTE_CONTROLLER,
  type Registration,
  createRequest,
  findRegistration,
  modelName,
  needsSdkCorrection,
  needsSdkMigration,
} from './devices';
import { reportError, reportThingStart } from './report';
import { ensureThingConnected, isThingConnected, thingBridgeState, type ThingTarget } from './thing';
import { pilotToken, revokeRetired } from './pilot_token';
import { loadApi, loadLiveshare, loadMap, loadMedia, loadMission, loadTsa, loadWs } from './modules';
import { banner, button, chip, el, formRow, panel, sleep, stateView } from './ui';
import { settingsPanel } from './settings';

interface Ctx {
  cfg: Config;
  session: Session;
  bridge: Bridge;
  acSn: string;
  rcSn: string;
  emqx: string;
  livekit: string;
  apiToken: string;
  registration: Registration;
  log: (line: string, dim?: boolean) => void;
}

export async function home(cfg: Config, session: Session, logoutButton: () => HTMLElement): Promise<void> {
  const app = document.getElementById('app')!;
  const bridge = findBridge();
  if (!bridge) {
    const wrap = el('div', 'login');
    wrap.append(
      stateView(
        'Open this page in DJI Pilot 2',
        'It links a DJI remote controller to Autrik and works only inside the DJI Pilot 2 app on the controller.',
        { action: logoutButton() },
      ),
    );
    app.replaceChildren(wrap);
    return;
  }
  // Pilot calls window[callback] after a thingConnect; the answer is logged only.
  window.connectCallback = (arg) => console.info('connectCallback', arg);

  const body = el('main', 'body');
  const page = el('div', 'page');
  page.append(body);
  app.replaceChildren(page);

  // Left: the controller panel, the account at its foot.
  const controller = panel('Controller');
  const facts = el('div', 'col tight');
  const register = el('div', 'col tight');
  const account = el('div', 'account');
  account.append(el('span', 'ac-dim ellipsis', session.email), logoutButton());
  controller.append(facts, register, el('div', 'ac-spacer'), account, el('div', 'ac-dim ac-t-bodySmall', `AutrikOS DJI ${cfg.appVersion}`));
  const fact = factRows(facts);

  // Right: one panel with a tab row (design system: five or fewer sections).
  const right = panel();
  const tabs = el('nav', 'ac-tabs');
  const modulesView = el('div');
  const settingsView = el('div');
  const logView = el('ol', 'log');
  // The Start tab (Pilot's raw answers) is for us, not for pilots: shown on
  // staging, and on prod only when the address ends in #debug. Prod reports
  // every start to Sentry either way.
  const debug = cfg.environment !== 'release' || location.hash === '#debug';
  const views: [string, HTMLElement][] = [
    ['Settings', settingsView],
    ['Modules', modulesView],
    ...(debug ? ([['Start', logView]] as [string, HTMLElement][]) : []),
  ];
  const tabButtons = views.map(([name]) => {
    const t = el('button', 'ac-tab', name);
    t.type = 'button';
    t.addEventListener('click', () => show(name));
    return t;
  });
  const show = (name: string) => {
    views.forEach(([n, v], i) => {
      v.hidden = n !== name;
      tabButtons[i]!.classList.toggle('is-selected', n === name);
    });
  };
  tabs.append(...tabButtons);
  logView.hidden = true;
  right.append(tabs, modulesView, settingsView, logView);
  // Settings first; a tab can be named in the address (#modules, #start), and #debug opens Start.
  const wanted = location.hash === '#debug' ? '#start' : location.hash;
  show(views.find(([n]) => `#${n.toLowerCase()}` === wanted)?.[0] ?? 'Settings');
  body.append(controller, right);
  const log = (line: string, dim = false) => logView.append(el('li', dim ? 'dim' : '', line));

  if (!session.workspaceId) fact('Workspace', chip('None', 'red', 'warning-circle'), 'This account has no workspace.');

  // 1. The DJI license, bound to the app id and key.
  try {
    if (!bridge.verifyLicense(cfg.djiAppId, cfg.djiAppKey, cfg.djiAppLicense)) {
      fact('License', chip('Not verified', 'red', 'x-circle'));
      log('DJI rejected the license: no module is loaded. Check the DJI app keys for this domain.');
      return;
    }
  } catch (e) {
    fact('License', chip('Error', 'red', 'x-circle'));
    reportError(e, 'platformVerifyLicense');
    log(String(e));
    return;
  }
  fact('License', chip('Verified', 'green', 'check'));

  // 2. What Pilot knows: serials and version; the log key; our name in Pilot.
  const acSn = safe(() => bridge.aircraftSn(), '');
  const rcSn = safe(() => bridge.remoteControllerSn(), '');
  const pilotVersion = safe(() => bridge.version(), '');
  if (cfg.logEncryptKey) {
    safe(() => bridge.clearLogEncryptKey(), null);
    safe(() => bridge.setLogEncryptKey(atob(cfg.logEncryptKey)), null);
  }
  safe(() => bridge.setInformation('AutrikOS', 'AutrikOS', 'Automated Drone Operations'), null);

  // 3. Tokens and the workspace's devices, in parallel.
  const [emqx, livekit, apiToken, devices] = await Promise.all([
    emqxToken(cfg).catch((e) => (reportError(e, 'service-token emqx'), '')),
    livekitToken(cfg).catch((e) => (reportError(e, 'service-token livekit'), '')),
    pilotToken(cfg).catch((e) => (reportError(e, 'refresh-session'), '')),
    session.workspaceId
      ? listDevices(cfg, session.workspaceId).catch((e): Device[] => (reportError(e, 'fetchDevices'), []))
      : Promise.resolve<Device[]>([]),
  ]);
  log(`tokens: mqtt ${emqx ? 'ok' : 'missing'}, video ${livekit ? 'ok' : 'missing'}, pilot ${apiToken ? 'ok' : 'missing'}`);

  const ctx: Ctx = {
    cfg,
    session,
    bridge,
    acSn,
    rcSn,
    emqx,
    livekit,
    apiToken,
    registration: findRegistration(devices, acSn, rcSn),
    log,
  };
  const showRegistration = () => {
    const state = (sn: string, d: Device | undefined) =>
      !sn ? chip('Not connected', 'grey', 'plugs') : d ? chip('Registered', 'green', 'check') : chip('Not registered', 'orange', 'warning');
    // The serial, and under it the model once Pilot has announced it (update_topo).
    const sub = (sn: string, d: Device | undefined) => (d ? `${sn}\n${modelName(d) ?? 'Model after first connection'}` : sn);
    fact('RC', state(rcSn, ctx.registration.rc), sub(rcSn, ctx.registration.rc));
    fact('Aircraft', state(acSn, ctx.registration.aircraft), sub(acSn, ctx.registration.aircraft));
  };
  showRegistration();
  fact('Pilot', el('span', '', pilotVersion || '—'));
  registrationForm(ctx, register, async () => {
    const fresh = await listDevices(cfg, session.workspaceId);
    ctx.registration = findRegistration(fresh, acSn, rcSn);
    showRegistration();
    // Pilot's session is refused until the RC is registered: bring it up again.
    if (ctx.registration.rc && !isThingConnected(bridge)) startThing(ctx, thingBridgeState(bridge));
  });
  await migrationDialogs(ctx);
  await correctSdk(ctx);

  // 4. The modules, in the Flutter page's order, 100 ms apart.
  if (session.workspaceId) safe(() => bridge.setWorkspaceId(session.workspaceId), null);
  // Read before the api module loads, in case loading it is what moves the
  // thing module (the start report shows both answers).
  const thingAtPageStart = thingBridgeState(bridge);
  load(ctx, 'api', () => loadApi(bridge, cfg, ctx.apiToken));
  await sleep(100);
  startThing(ctx, thingAtPageStart);
  await sleep(100);
  load(ctx, 'liveshare', () => loadLiveshare(bridge, cfg, streamId(ctx), livekit));
  await sleep(100);
  load(ctx, 'ws', () => loadWs(bridge, cfg, ctx.apiToken));
  await sleep(100);
  load(ctx, 'media', () => loadMedia(bridge, { autoUploadPhoto: stored('autoUploadPhoto'), autoUploadVideo: stored('autoUploadVideo') }));
  await sleep(100);
  load(ctx, 'mission', () => loadMission(bridge));
  await sleep(100);
  load(ctx, 'tsa', () => loadTsa(bridge));
  await sleep(100);
  load(ctx, 'map', () => loadMap(bridge));
  log('modules loaded');
  // The previous starts' tokens end only now that the modules carry the new
  // one, and after a grace period for requests Pilot already had in flight: a
  // 401 on a module request makes Pilot leave the webview.
  if (apiToken) setTimeout(() => void revokeRetired(cfg, (e) => reportError(e, 'revoke-previous-session')), 30_000);

  modulesGrid(ctx, modulesView);
  settingsView.append(settingsPanel(bridge));
}

/** Load one module; a refusal goes to the Start tab with Pilot's answer, and to Sentry. */
function load(ctx: Ctx, name: string, f: () => void): void {
  try {
    f();
    ctx.log(`${name}: loaded`, true);
  } catch (e) {
    reportError(e, `load ${name}`);
    ctx.log(`${name}: load failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

function thingTarget(ctx: Ctx): ThingTarget {
  return { host: ctx.cfg.telemetryHostCloudApi, deviceSn: ctx.rcSn, token: ctx.emqx };
}

function startThing(ctx: Ctx, atPageStart: string): void {
  if (!ctx.rcSn || !ctx.emqx) {
    ctx.log(`telemetry not started: ${ctx.rcSn ? 'no MQTT token' : 'no RC serial'}`);
    return;
  }
  void ensureThingConnected(
    {
      bridge: ctx.bridge,
      sleep,
      report: (outcome, sn, trace) => {
        reportThingStart(outcome, sn, trace);
        // The page shows Pilot's answers too: dev has no Sentry.
        for (const line of trace) ctx.log(`  ${line}`, true);
      },
    },
    thingTarget(ctx),
    atPageStart,
  ).then((outcome) => ctx.log(`telemetry: ${outcome}`));
}

/** The video stream id: the RC's, as the Flutter page took it after registering. */
function streamId(ctx: Ctx): string {
  return ctx.registration.rc?.stream_id ?? '';
}

/** Form rows that can be set again: a label, a state chip, and a dim sub-value (a serial). */
function factRows(box: HTMLElement) {
  const rows = new Map<string, HTMLElement>();
  return (name: string, state: HTMLElement, sub?: string) => {
    let value = rows.get(name);
    if (!value) {
      value = el('span', 'ac-row');
      rows.set(name, value);
      box.append(formRow(name, value));
    }
    value.replaceChildren(state);
    if (sub) value.append(el('span', 'ac-dim', sub));
  };
}

/**
 * Register the serials Pilot reports that the workspace does not have yet,
 * by role only: the model follows from Pilot's first update_topo (devices.ts
 * modelName), so there is nothing to pick.
 */
function registrationForm(ctx: Ctx, box: HTMLElement, onRegistered: () => Promise<void>): void {
  const render = () => {
    box.replaceChildren();
    const missing: { name: 'Drone' | 'RC'; sn: string; role: DeviceModel }[] = [];
    if (ctx.acSn && !ctx.registration.aircraft) missing.push({ name: 'Drone', sn: ctx.acSn, role: AIRCRAFT });
    if (ctx.rcSn && !ctx.registration.rc) missing.push({ name: 'RC', sn: ctx.rcSn, role: REMOTE_CONTROLLER });
    if (!missing.length) return;
    const hint = banner('info');
    box.append(hint.el);
    if (ctx.session.role !== 'admin') {
      hint.set('Not registered in this workspace. Ask a workspace admin to register this controller.');
      return;
    }
    hint.set(
      `Register ${missing.map((m) => (m.name === 'RC' ? 'the controller' : 'the aircraft')).join(' and ')} in your workspace so the platform accepts the telemetry. The model is read from the controller once it connects.`,
    );
    const error = banner('error');
    const go = button(
      'Register Devices',
      async () => {
        error.set('');
        go.disabled = true;
        try {
          for (const m of missing) {
            await createDevice(ctx.cfg, ctx.session.workspaceId, createRequest(m.name, m.sn, m.role));
            ctx.log(`registered ${m.name === 'RC' ? 'RC' : 'aircraft'} ${m.sn}`);
          }
          await onRegistered();
          render();
        } catch (e) {
          reportError(e, 'createDevice');
          error.set(`Registration failed: ${e instanceof Error ? e.message : String(e)}. Check the connection and register again.`);
          go.disabled = false;
        }
      },
      'filled',
    );
    const actions = el('div', 'actions');
    actions.append(go);
    box.append(error.el, actions);
    if (!ctx.acSn) box.append(el('div', 'ac-dim ac-t-bodySmall', 'The aircraft is off. Power it on and reload the page to register it as well.'));
  };
  render();
}

/** A device registered with sdk "DJI" is set to DJI_CLOUDAPI, so the dashboard shows its telemetry. */
async function correctSdk(ctx: Ctx): Promise<void> {
  for (const { device, label } of needsSdkCorrection(ctx.registration)) {
    try {
      await setDeviceSdk(ctx.cfg, ctx.session.workspaceId, device.device_id!, 'DJI_CLOUDAPI');
      device.sdk = 'DJI_CLOUDAPI';
      ctx.log(`${label} ${device.device_sn}: SDK DJI → DJI_CLOUDAPI`);
    } catch (e) {
      reportError(e, 'updateDevice sdk DJI');
      ctx.log(`${label} ${device.device_sn}: SDK correction failed: ${String(e)}`);
    }
  }
}

/** One dialog per device bound through the AutrikOS DJI app; the primary action moves it to DJI_CLOUDAPI. */
async function migrationDialogs(ctx: Ctx): Promise<void> {
  for (const { device, label } of needsSdkMigration(ctx.registration, ctx.acSn, ctx.rcSn)) {
    const noun = label === 'RC' ? 'controller' : 'aircraft';
    const migrate = await new Promise<boolean>((resolve) => {
      const overlay = el('div', 'overlay ac');
      const d = el('div', 'ac-dialog');
      const actions = el('div', 'ac-dialog__actions');
      actions.append(
        button('Keep in AutrikOS DJI App', () => (overlay.remove(), resolve(false)), 'text'),
        button('Move to DJI Pilot 2', () => (overlay.remove(), resolve(true)), 'filled'),
      );
      d.append(
        el('h2', 'ac-dialog__title', `Move the ${noun} to DJI Pilot 2?`),
        el(
          'div',
          'ac-dialog__body',
          `The ${noun} ${device.device_sn} was bound to Autrik with the AutrikOS DJI App. ` +
            'Moving it sets its SDK to DJI Pilot 2 (Cloud API).',
        ),
        actions,
      );
      overlay.append(d);
      document.body.append(overlay);
    });
    if (!migrate) continue;
    try {
      await setDeviceSdk(ctx.cfg, ctx.session.workspaceId, device.device_id!, 'DJI_CLOUDAPI');
      device.sdk = 'DJI_CLOUDAPI';
      ctx.log(`${label} ${device.device_sn}: SDK set to DJI_CLOUDAPI`);
    } catch (e) {
      reportError(e, 'updateDevice sdk');
      ctx.log(`${label} ${device.device_sn}: migration failed: ${String(e)}`);
    }
  }
}

interface ModuleRow {
  label: string;
  name: string; // Pilot's module name
  connected?: () => boolean; // a second state for modules with a session
}

/** One state per module, in words: a module with a session says whether it is up. */
function moduleState(loaded: boolean, connected: boolean | undefined): HTMLElement {
  if (!loaded) return chip('Not loaded', 'grey');
  if (connected === undefined) return chip('Loaded', 'green');
  return connected ? chip('Connected', 'green') : chip('Not connected', 'orange');
}

/** The modules as a grid, a status chip each, read from Pilot every 2 s (T90 §4.6). The page loads them; nothing to press. */
function modulesGrid(ctx: Ctx, view: HTMLElement): void {
  const { bridge } = ctx;
  const rows: ModuleRow[] = [
    { label: 'Telemetry', name: 'thing', connected: () => isThingConnected(bridge) },
    {
      label: 'Video',
      name: 'liveshare',
      connected: () => {
        const r = bridge.reply('liveshareGetStatus');
        if (r.code !== 0 || typeof r.data !== 'string') return false;
        return (JSON.parse(r.data) as { fps?: number }).fps !== -1;
      },
    },
    {
      label: 'WebSocket',
      name: 'ws',
      connected: () => {
        const r = bridge.wsGetConnectState();
        return r.code === 0 && r.data === true;
      },
    },
    { label: 'Media', name: 'media' },
    { label: 'Mission', name: 'mission' },
    { label: 'TSA', name: 'tsa' },
    { label: 'Map', name: 'map' },
  ];
  const table = el('table', 'ac-grid');
  const head = el('tr');
  for (const h of ['Module', 'Status']) head.append(el('th', '', h));
  const thead = el('thead');
  thead.append(head);
  const tbody = el('tbody');
  table.append(thead, tbody);
  const refreshers: (() => void)[] = [];
  for (const r of rows) {
    const tr = el('tr');
    const stateCell = el('td');
    tr.append(el('td', '', r.label), stateCell);
    tbody.append(tr);
    const refresh = () => {
      const loaded = safe(() => bridge.isComponentLoaded(r.name), false);
      stateCell.replaceChildren(moduleState(loaded, r.connected ? safe(r.connected, false) : undefined));
    };
    refreshers.push(refresh);
  }
  view.append(table);
  const tick = () => refreshers.forEach((f) => f());
  tick();
  setInterval(tick, 2000);
}

function safe<T>(f: () => T, fallback: T): T {
  try {
    return f();
  } catch (e) {
    console.warn(e);
    return fallback;
  }
}

function stored(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}
